<template>
  <div class="screen-tab-focus-view-actions">
    <div class="d-flex align-center">
      <researcher-avatar-cluster
        v-if="researchersToShow.length"
        class="mr-1"
        :researchers="researchersToShow"
        :current-researcher="currentResearcher"
      />
      <!-- The label that appears above the vote buttons -->
      <span class="label">
        <span class="voter-names">
          {{ voterNamesLabel }}
        </span>

        <!-- "Is this relevant" -->
        <template v-if="showPendingActionText">
          <template v-if="researchersToShow.length">
            {{ votedOnLabel }} this.
          </template>
          <template v-if="!showNoFurtherActionText">
            Is this relevant?
          </template>
        </template>

        <!-- "You previously voted this reference as [icon] [label]. Have you changed your mind?" -->
        <template v-else-if="showPreviousVoteText">
          previously {{ votedLabel }} this reference as
          <v-icon
            class="material-icons-outlined"
            :color="previousVoteIconColor"
            small
          >
            {{ previousVoteIcon }}
          </v-icon>
          {{ previousVoteLabel }}.
          <template v-if="!showNoFurtherActionText">
            Have you changed your mind?
          </template>
        </template>

        <!-- "[users] have conflicting votes, please resolve the conflict." -->
        <template v-else-if="showConflictResolutionText">
          <template v-if="!showNoFurtherActionText">
            have conflicting votes, please resolve the conflict.
          </template>
          <template v-else>have conflicting votes.</template>
        </template>

        <!-- "[users] screened this as [icon] [label] by consensus. Would you like to change this?" -->
        <template v-else-if="showConsensusOutcomeText">
          {{ screenedLabel }} this as
          <v-icon
            class="material-icons-outlined"
            :color="outcomeIconColor"
            small
          >
            {{ outcomeIcon }}
          </v-icon>
          <!-- Formatting is horrible but necessary to avoid extra whitespace >:( -->
          {{ outcomeLabel
          }}<template v-if="researchersToShow.length > 1">
            by consensus</template
          >.
          <template v-if="!showNoFurtherActionText">
            Would you like to change this?
          </template>
        </template>

        <!-- "[user] screened this as [icon] [label] by conflict resolution. Would you like to change this?" -->
        <template v-else-if="showConflictResolutionOutcomeText">
          {{ screenedLabel }} this as
          <v-icon
            class="material-icons-outlined"
            :color="outcomeIconColor"
            small
          >
            {{ outcomeIcon }}
          </v-icon>
          {{ outcomeLabel }} by conflict resolution.
          <template v-if="!showNoFurtherActionText">
            Would you like to change this?
          </template>
        </template>

        <!-- "[user] screened this as [icon] [label] by manual override. Would you like to change this?" -->
        <template v-else-if="showOverrideOutcomeText">
          {{ screenedLabel }} this as
          <v-icon
            class="material-icons-outlined"
            :color="outcomeIconColor"
            small
          >
            {{ outcomeIcon }}
          </v-icon>
          {{ outcomeLabel }} by manual override.
          <template v-if="!showNoFurtherActionText">
            Would you like to change this?
          </template>
        </template>

        <!-- "[user] pre-screened this as [icon] [label] on upload. Would you like to change this?" -->
        <template v-else-if="showUploadOutcomeText">
          pre-screened this as
          <v-icon
            class="material-icons-outlined"
            :color="outcomeIconColor"
            small
          >
            {{ outcomeIcon }}
          </v-icon>
          {{ outcomeLabel }} on upload.
          <template v-if="!showNoFurtherActionText">
            Would you like to change this?
          </template>
        </template>

        <!-- "[user] screened this as [icon] [label] after system suggestion. Would you like to change this?" -->
        <template v-if="showSuggestionOutcomeText">
          {{ screenedLabel }} this as
          <v-icon
            class="material-icons-outlined"
            :color="outcomeIconColor"
            small
          >
            {{ outcomeIcon }}
          </v-icon>
          {{ outcomeLabel }} after system suggestion.
          <template v-if="!showNoFurtherActionText">
            Would you like to change this?
          </template>
        </template>

        <!-- "No further action required from you." + tooltip -->
        <template v-if="showNoFurtherActionText">
          No further action required from you.
          <v-tooltip top>
            <template #activator="{ on, attrs }">
              <v-icon v-bind="attrs" small color="primary" v-on="on">
                help
              </v-icon>
            </template>
            <span>{{ noFurtherActionTooltip }}</span>
          </v-tooltip>
        </template>
      </span>
    </div>

    <!-- The vote buttons -->
    <vote-controls
      show-labels
      large
      :disabled="disableButtons"
      :full-text-review="fullTextReview"
      @vote="onVote"
    />

    <v-btn
      v-if="canMarkAsDuplicate"
      small
      text
      color="primary"
      @click="duplicateModalOpen = true"
    >
      <v-icon left small>content_copy</v-icon>
      Mark as duplicate
    </v-btn>

    <!-- Modal that asks for a reason for excluding a reference during full text review -->
    <not-relevant-review-modal
      v-model="showExcludeModal"
      :review="review"
      @save="onFullTextExclude"
    />

    <!-- Modal for manually marking this reference as a duplicate of another -->
    <mark-as-duplicate-modal
      :value="duplicateModalOpen"
      :reference="reference"
      @input="duplicateModalOpen = $event"
      @marked="onDuplicateMarked"
    />
  </div>
</template>

<script>
import VoteControls from '@/components/ScreenReferencesPage/VoteControls';
import ResearcherAvatarCluster from '@/components/Common/ResearcherAvatarCluster';
import NotRelevantReviewModal from '@/components/FullTextReviewPage/NotRelevantReviewModal';
import MarkAsDuplicateModal from '@/components/References/MarkAsDuplicateModal';
import {
  Reference,
  Review,
  Researcher,
  ReviewerScreeningVote,
  ScreeningOutcome,
  FullTextReviewVote,
  FullTextReviewOutcome,
} from '@/models';
import {
  ReferenceActionState,
  RelevanceAssessment,
  InclusionAssessment,
  ScreeningOutcomeResolution,
} from '@/enums';
import { mapActions } from 'vuex';
import { cloneDeep } from 'lodash';

const ASSESSMENT_TO_PROPS = {
  [RelevanceAssessment.RELEVANT]: {
    color: 'success',
    icon: 'thumb_up',
    label: 'Relevant',
  },
  [RelevanceAssessment.NOT_SURE]: {
    color: 'warning',
    icon: 'help_outline',
    label: 'Not Sure',
  },
  [RelevanceAssessment.NOT_RELEVANT]: {
    color: 'error',
    icon: 'thumb_down',
    label: 'Not Relevant',
  },
  [InclusionAssessment.INCLUDE]: {
    color: 'success',
    icon: 'thumb_up',
    label: 'Include',
  },
  [InclusionAssessment.EXCLUDE]: {
    color: 'error',
    icon: 'thumb_down',
    label: 'Exclude',
  },
};

const VOTE_CONTROLS_VALUE_TO_SCREENING_ASSESSMENT = {
  YES: RelevanceAssessment.RELEVANT,
  NO: RelevanceAssessment.NOT_RELEVANT,
  NOT_SURE: RelevanceAssessment.NOT_SURE,
};

const VOTE_CONTROLS_VALUE_TO_REVIEW_ASSESSMENT = {
  YES: InclusionAssessment.INCLUDE,
  NO: InclusionAssessment.EXCLUDE,
};

const VOTING_ONLY_VALID_STATES = [
  ReferenceActionState.PENDING_ACTION,
  ReferenceActionState.PENDING_ACTION_BY_YOU,
  ReferenceActionState.PENDING_ACTION_ALREADY_TAKEN,
];

const STATE_TO_PROPS = {
  [ReferenceActionState.PENDING_ACTION]: {
    showPendingActionText: true,
    actionMethod: 'onCreateVote',
  },
  [ReferenceActionState.PENDING_ACTION_BY_YOU]: {
    showPendingActionText: true,
    actionMethod: 'onCreateVote',
  },
  [ReferenceActionState.PENDING_ACTION_BY_OTHERS]: {
    showNoFurtherActionText: true,
    noFurtherActionTooltip:
      'This reference is pending action by other reviewers.',
    showPreviousVoteText: true,
    disableButtons: true,
  },
  [ReferenceActionState.PENDING_ACTION_ALREADY_TAKEN]: {
    showPreviousVoteText: true,
    actionMethod: 'onUpdateVote',
  },
  [ReferenceActionState.PENDING_CONFLICT_RESOLUTION_BY_YOU]: {
    showConflictResolutionText: true,
    actionMethod: 'onResolveConflict',
  },
  [ReferenceActionState.PENDING_CONFLICT_RESOLUTION_BY_OTHERS]: {
    showConflictResolutionText: true,
    showNoFurtherActionText: true,
    noFurtherActionTooltip:
      'You do not have the permissions to resolve this conflict.',
    disableButtons: true,
  },
  [ReferenceActionState.INCLUDED_BY_CONSENSUS]: {
    showConsensusOutcomeText: true,
    actionMethod: 'onUpdateOutcome',
  },
  [ReferenceActionState.EXCLUDED_BY_CONSENSUS]: {
    showConsensusOutcomeText: true,
    actionMethod: 'onUpdateOutcome',
  },
  [ReferenceActionState.INCLUDED_BY_CONFLICT_RESOLUTION]: {
    showConflictResolutionOutcomeText: true,
    actionMethod: 'onUpdateOutcome',
  },
  [ReferenceActionState.EXCLUDED_BY_CONFLICT_RESOLUTION]: {
    showConflictResolutionOutcomeText: true,
    actionMethod: 'onUpdateOutcome',
  },
  [ReferenceActionState.INCLUDED_BY_OVERRIDE]: {
    showOverrideOutcomeText: true,
    actionMethod: 'onUpdateOutcome',
  },
  [ReferenceActionState.EXCLUDED_BY_OVERRIDE]: {
    showOverrideOutcomeText: true,
    actionMethod: 'onUpdateOutcome',
  },
  [ReferenceActionState.INCLUDED_ON_UPLOAD]: {
    showUploadOutcomeText: true,
    actionMethod: 'onUpdateOutcome',
  },
  [ReferenceActionState.EXCLUDED_ON_UPLOAD]: {
    showUploadOutcomeText: true,
    actionMethod: 'onUpdateOutcome',
  },
  [ReferenceActionState.EXCLUDED_BY_SUGGESTION]: {
    showSuggestionOutcomeText: true,
    actionMethod: 'onUpdateOutcome',
  },
};

export default {
  name: 'ScreenTabFocusViewActions',
  components: {
    VoteControls,
    ResearcherAvatarCluster,
    NotRelevantReviewModal,
    MarkAsDuplicateModal,
  },
  props: {
    reference: {
      type: Reference,
      required: true,
    },
    review: {
      type: Review,
      required: true,
    },
    currentResearcher: {
      type: Researcher,
      required: true,
    },
    votingOnly: {
      type: Boolean,
      default: false,
    },
    fullTextReview: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'saving-vote',
    'saved-vote',
    'saving-outcome',
    'saved-outcome',
    'marked',
  ],
  data: () => ({
    showExcludeModal: false,
    duplicateModalOpen: false,
  }),
  computed: {
    votedOnLabel() {
      return this.fullTextReview ? 'reviewed' : 'voted on';
    },
    votedLabel() {
      return this.fullTextReview ? 'reviewed' : 'voted';
    },
    votesLabel() {
      return this.fullTextReview ? 'reviews' : 'votes';
    },
    screenedLabel() {
      return this.fullTextReview ? 'reviewed' : 'screened';
    },
    votes() {
      return this.fullTextReview
        ? this.reference.fullTextReviewVotes
        : this.reference.reviewerScreeningVotes;
    },
    voterNamesLabel() {
      if (this.showOutcomeResolver) return this.outcomeNameLabel;

      const hasVotesFromYou = this.hasVoteByResearcher(
        this.currentResearcher
      );

      const otherVoters = this.votes
        .map((vote) => vote.reviewer?.researcher)
        .filter(
          (researcher) =>
            researcher && researcher.id !== this.currentResearcher.id
        )
        .slice(0, 3);

      if (hasVotesFromYou) {
        if (this.votingOnly) {
          return 'You';
        }
        return ['You', otherVoters[0]?.firstName()]
          .filter(Boolean)
          .join(' and ');
      } else {
        return otherVoters
          .map((researcher) => researcher.firstName())
          .join(' and ');
      }
    },
    researchersToShow() {
      if (this.showOutcomeResolver) {
        return [this.outcome.resolvedBy.researcher];
      }
      return this.votes
        .map((vote) => vote.reviewer?.researcher)
        .filter(Boolean)
        .slice(0, 3);
    },
    outcomeNameLabel() {
      const reviewMember = this.outcome.resolvedBy;
      const researcher = reviewMember.researcher;
      return researcher.id === this.currentResearcher.id
        ? 'You'
        : this.outcome.resolvedBy.researcher.firstName();
    },
    showOutcomeResolver() {
      return !this.votingOnly && !!this.outcome?.resolvedBy;
    },
    showPendingActionText() {
      return this.stateProps.showPendingActionText;
    },
    showNoFurtherActionText() {
      return this.stateProps.showNoFurtherActionText ||
        this.fullTextReview
        ? !this.reference.isPendingExtraction()
        : !this.reference.isPendingFullTextReview();
    },
    noFurtherActionTooltip() {
      return this.stateProps.noFurtherActionTooltip ||
        this.fullTextReview
        ? 'Data extraction has started for this reference, so you can no longer modify its full text review state.'
        : 'Full text review has started for this reference, so you can no longer modify its screening state.';
    },
    showPreviousVoteText() {
      return this.stateProps.showPreviousVoteText;
    },
    showConflictResolutionText() {
      return this.stateProps.showConflictResolutionText;
    },
    showConsensusOutcomeText() {
      return this.stateProps.showConsensusOutcomeText;
    },
    showConflictResolutionOutcomeText() {
      return this.stateProps.showConflictResolutionOutcomeText;
    },
    showOverrideOutcomeText() {
      return this.stateProps.showOverrideOutcomeText;
    },
    showUploadOutcomeText() {
      return this.stateProps.showUploadOutcomeText;
    },
    showSuggestionOutcomeText() {
      return this.stateProps.showSuggestionOutcomeText;
    },
    nextStageOfReviewInProgressForThisReference() {
      return this.fullTextReview
        ? !this.reference.isPendingExtraction()
        : !this.reference.isPendingFullTextReview();
    },
    disableButtons() {
      return (
        this.stateProps.disableButtons ||
        this.nextStageOfReviewInProgressForThisReference
      );
    },
    actionMethod() {
      return this.stateProps.actionMethod;
    },
    previousVoteIconColor() {
      return ASSESSMENT_TO_PROPS[this.previousVoteByYou?.vote]?.color;
    },
    previousVoteIcon() {
      return ASSESSMENT_TO_PROPS[this.previousVoteByYou?.vote]?.icon;
    },
    previousVoteLabel() {
      return ASSESSMENT_TO_PROPS[this.previousVoteByYou?.vote]?.label;
    },
    previousVoteByYou() {
      return this.getVoteByResearcher(this.currentResearcher);
    },
    outcomeIcon() {
      return ASSESSMENT_TO_PROPS[this.outcome?.outcome]?.icon;
    },
    outcomeIconColor() {
      return ASSESSMENT_TO_PROPS[this.outcome?.outcome]?.color;
    },
    outcomeLabel() {
      return ASSESSMENT_TO_PROPS[this.outcome?.outcome]?.label;
    },
    outcome() {
      return this.fullTextReview
        ? this.reference.fullTextReviewOutcome
        : this.reference.screeningOutcome;
    },
    stateProps() {
      return STATE_TO_PROPS[this.actionState];
    },
    actionState() {
      const actionState = this.getActionState(
        this.review,
        this.currentResearcher
      );
      if (this.votingOnly) {
        if (VOTING_ONLY_VALID_STATES.includes(actionState)) {
          return actionState;
        }
        return ReferenceActionState.PENDING_ACTION_ALREADY_TAKEN;
      }
      return actionState;
    },
    currentReviewer() {
      return this.review.reviewMembers.find(
        (reviewer) =>
          reviewer.researcher.id === this.currentResearcher.id
      );
    },
    optimisticReadyReference() {
      // When saving a vote, we need to pass in a reference that has a more complete version of `review`.
      // This is because the `review` field on the reference does not have all the information we need
      // to save the vote optimistically.
      const reference = cloneDeep(this.reference);
      reference.review = this.review;
      return reference;
    },
    voteType() {
      return this.fullTextReview
        ? FullTextReviewVote
        : ReviewerScreeningVote;
    },
    outcomeType() {
      return this.fullTextReview
        ? FullTextReviewOutcome
        : ScreeningOutcome;
    },
    canMarkAsDuplicate() {
      // Hidden during full text review: by that stage the reference has passed
      // screening and merging it away is disruptive. Permission is enforced by
      // the server regardless; this stops users hitting an avoidable error.
      return (
        !this.fullTextReview &&
        !!this.review?.currentUserCanEditReview?.()
      );
    },
  },
  methods: {
    ...mapActions({
      saveScreeningVoteOptimistically:
        'reviewerScreeningVote/saveOptimistically',
      saveScreeningOutcome: 'screeningOutcome/saveOptimistically',
      resolveScreeningConflict:
        'screeningOutcome/resolveConflictOptimistically',
      saveFullTextReviewVoteOptimistically:
        'fullTextReviewVote/saveOptimistically',
      saveFullTextReviewOutcome:
        'fullTextReviewOutcome/saveOptimistically',
      resolveFullTextReviewConflict:
        'fullTextReviewOutcome/resolveConflictOptimistically',
    }),
    getActionState(review, researcher) {
      return this.fullTextReview
        ? this.reference.getFullTextReviewActionState(
            review,
            researcher
          )
        : this.reference.getScreeningActionState(review, researcher);
    },
    getVoteByResearcher(researcher) {
      return this.fullTextReview
        ? this.reference.getFullTextReviewVoteBy(researcher)
        : this.reference.getScreeningVoteBy(researcher);
    },
    hasVoteByResearcher(researcher) {
      return !!this.getVoteByResearcher(researcher);
    },
    saveVoteOptimistically(voteToSave) {
      return this.fullTextReview
        ? this.saveFullTextReviewVoteOptimistically(voteToSave)
        : this.saveScreeningVoteOptimistically(voteToSave);
    },
    saveOutcome(outcomeToSave) {
      return this.fullTextReview
        ? this.saveFullTextReviewOutcome(outcomeToSave)
        : this.saveScreeningOutcome(outcomeToSave);
    },
    resolveConflict(outcomeToSave) {
      return this.fullTextReview
        ? this.resolveFullTextReviewConflict(outcomeToSave)
        : this.resolveScreeningConflict(outcomeToSave);
    },
    async saveVote(voteToSave) {
      this.$emit('saving-vote', voteToSave);
      const existingVote = this.getVoteByResearcher(
        this.currentResearcher
      );
      let savedVote = voteToSave;
      // Don't save the vote if it's the same as the existing vote
      if (existingVote?.vote !== voteToSave.vote) {
        savedVote = await this.saveVoteOptimistically(voteToSave);
      }
      this.$emit('saved-vote', savedVote);
    },
    onVote(vote) {
      const voteToAssessment = this.fullTextReview
        ? VOTE_CONTROLS_VALUE_TO_REVIEW_ASSESSMENT
        : VOTE_CONTROLS_VALUE_TO_SCREENING_ASSESSMENT;
      const assessment = voteToAssessment[vote];
      if (assessment === InclusionAssessment.EXCLUDE) {
        this.showExcludeModal = true;
      } else {
        this[this.actionMethod](assessment);
      }
    },
    async onCreateVote(vote, { reviewCriteria, notes } = {}) {
      const voteToSave = new this.voteType({
        reference: this.optimisticReadyReference,
        reviewer: this.currentReviewer,
        vote,
        reasonReviewCriteria: reviewCriteria,
        notes,
      });
      this.saveVote(voteToSave);
    },
    onUpdateVote(vote, { reviewCriteria, notes } = {}) {
      const existingVote = this.getVoteByResearcher(
        this.currentResearcher
      );
      if (!existingVote) throw new Error('No existing vote found');
      const voteToSave = new this.voteType({
        ...existingVote,
        reference: this.optimisticReadyReference,
        vote,
        reasonReviewCriteria: reviewCriteria,
        notes,
      });
      this.saveVote(voteToSave);
    },
    async onResolveConflict(outcome, { reviewCriteria, notes } = {}) {
      const outcomeToSave = {
        reference: this.optimisticReadyReference,
        outcome,
        reasonReviewCriteria: reviewCriteria,
        notes,
      };
      this.$emit('saving-outcome', outcomeToSave);
      const outcomeSaved = await this.resolveConflict(outcomeToSave);
      this.$emit('saved-outcome', outcomeSaved);
    },
    async onUpdateOutcome(outcome, { reviewCriteria, notes } = {}) {
      const screeningOutcome = this.outcome;
      if (!screeningOutcome)
        throw new Error('No existing outcome found');

      const wasConflict =
        screeningOutcome.resolution ===
        ScreeningOutcomeResolution.CONFLICT_RESOLUTION;
      const wasByCurrentUser =
        screeningOutcome.resolvedBy?.researcher.id ===
        this.currentResearcher.id;

      // Keep it marked as conflict resolution if it was already
      // marked as such by the current user.
      const newResolution =
        wasConflict && wasByCurrentUser
          ? ScreeningOutcomeResolution.CONFLICT_RESOLUTION
          : ScreeningOutcomeResolution.UPDATED_OUTCOME;

      const outcomeToSave = new this.outcomeType({
        ...screeningOutcome,
        outcome,
        reference: this.optimisticReadyReference,
        resolution: newResolution,
        reasonReviewCriteria: reviewCriteria,
        notes,
      });
      this.$emit('saving-outcome', outcomeToSave);
      const outcomeSaved = await this.saveOutcome(outcomeToSave);
      this.$emit('saved-outcome', outcomeSaved);
    },
    async onFullTextExclude({ reviewCriteria, notes }) {
      this[this.actionMethod](InclusionAssessment.EXCLUDE, {
        reviewCriteria,
        notes,
      });
    },
    onDuplicateMarked(merge) {
      this.duplicateModalOpen = false;
      this.$emit('marked', merge);
    },
  },
};
</script>

<style lang="scss" scoped>
.screen-tab-focus-view-actions {
  display: grid;
  place-items: center;
  gap: 12px;

  .label {
    font-size: 12px;
    font-weight: 600;
    line-height: 14.4px;
  }
}
</style>
